// client/src/pages/Leads.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import API from '../api/client';
import AdminHrDashboard from '../pages/AdminHrDashboard';
import {
    Box, Typography, Button, Paper, Alert, CircularProgress, Grid, MenuItem,
    TextField, Chip, Select, Fade, IconButton, Stack, Checkbox,
    Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, Tab, Divider, Card, CardContent, TablePagination, Tooltip
} from '@mui/material';
import {
    Search, X, Download, RefreshCw, Copy, Check, Briefcase, Info,
    ArrowLeft, Filter, Layers, Users, CheckCircle, TrendingUp, ChevronRight
} from 'lucide-react';

const SF_THEME = {
    globalHeaderBg: '#091A3E',
    utilityBarBg: '#f3f3f3',
    canvasBg: '#f3f2f1',
    cardBg: '#ffffff',
    borderStandard: '#dddbda',
    textPrimary: '#181818',
    textSecondary: '#514f4d',
    accentBrand: '#0176d3',
    accentHover: '#014486',
    successGreen: '#2e844a',
    warningAmber: '#b78103',
    errorRed: '#ea001e'
};

const PIPELINE_STAGES = ['Pending', 'In Progress', 'Converted', 'Not Interested'];

const SF_STATUS_MAP = {
    'converted': { label: 'Closed - Converted', color: '#ffffff', bg: SF_THEME.successGreen },
    'in progress': { label: 'Working / Inprogress', color: '#ffffff', bg: SF_THEME.accentBrand },
    'not interested': { label: 'Lead Lost', color: '#ffffff', bg: '#eb080890' },
    'pending': { label: 'New Lead', color: '#ffffff', bg: SF_THEME.warningAmber }
};

const resolveSfStatus = (status) => {
    const cleanKey = String(status || '').trim().toLowerCase();
    return SF_STATUS_MAP[cleanKey] || SF_STATUS_MAP['pending'];
};

const LightningDataField = ({ fieldLabel, fieldValue }) => {
    const [copied, setCopied] = useState(false);

    const executeCopy = (e) => {
        e.stopPropagation();
        if (!fieldValue || fieldValue === '—') return;
        navigator.clipboard.writeText(fieldValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ p: 2, borderBottom: `1px solid ${SF_THEME.borderStandard}`, '&:hover': { bgcolor: '#fbfaf9' } }}>
            <Typography sx={{ color: SF_THEME.textSecondary, fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {fieldLabel}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5, minHeight: '24px' }}>
                <Typography sx={{ color: SF_THEME.textPrimary, fontSize: '14px', fontWeight: 500 }}>
                    {fieldValue || '—'}
                </Typography>
                {fieldValue && fieldValue !== '—' && (
                    <IconButton size="small" onClick={executeCopy} sx={{ p: 0.2, color: SF_THEME.textSecondary, '&:hover': { color: SF_THEME.accentBrand } }}>
                        {copied ? <Check size={14} color={SF_THEME.successGreen} /> : <Copy size={14} />}
                    </IconButton>
                )}
            </Stack>
        </Box>
    );
};

const Leads = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeLead, setActiveLead] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [stageFilter, setStageFilter] = useState('ALL');
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [systemAlert, setSystemAlert] = useState({ body: '', type: 'info' });
    const [activeWorkspaceTab, setActiveWorkspaceTab] = useState(0);
    const [employeeMap, setEmployeeMap] = useState({});

    // Pagination Parameters
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [currentUserContext] = useState(() => {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : { role: 'BDE' };
    });

    const isSupervisorOrHR = useMemo(() => {
        const role = String(currentUserContext?.role || '').trim().toUpperCase();
        return role === 'ADMIN' || role === 'HR';
    }, [currentUserContext]);

    const reloadSalesforceDataRegistry = useCallback(async () => {
        try {
            setLoading(true);
            setSystemAlert({ body: '', type: 'info' });

            // 1. Fetch normal pipeline leads (Allowed for both roles)
            try {
                const leadsResponse = await API.get('/leads');
                const dataPayload = leadsResponse.data.leads || leadsResponse.data;
                const recordsList = Array.isArray(dataPayload) ? dataPayload : [];
                setLeads(recordsList);
            } catch (leadErr) {
                console.error("Leads fetch exception:", leadErr);
                setSystemAlert({ body: 'Network pipeline unable to load master logs from engine.', type: 'error' });
            }

            // 🛡️ 2. CRITICAL ROLE GUARD: Only pull global administrative employee list if authorized
            if (isSupervisorOrHR) {
                try {
                    const employeesResponse = await API.get('/employees');
                    const employeesList = employeesResponse.data?.employees || employeesResponse.data?.data || employeesResponse.data || [];

                    if (Array.isArray(employeesList)) {
                        const lookupMap = {};
                        employeesList.forEach(emp => {
                            const empId = emp.id || emp._id;
                            const empName = emp.name || emp.username || emp.email;
                            if (empId && empName) lookupMap[String(empId)] = String(empName);
                        });
                        setEmployeeMap(lookupMap);
                    }
                } catch (empErr) {
                    console.error("Employee registry mapping exception:", empErr);
                }
            } else {
                console.log("Skipping /api/employees pipeline fetch sequence: Identity Context lacks [ADMIN, HR] privileges.");
            }
        } finally {
            setLoading(false);
        }
    }, [isSupervisorOrHR]); // Added to dependency matrix array for safety

    useEffect(() => { reloadSalesforceDataRegistry(); }, [reloadSalesforceDataRegistry]);

    // Compute Lead Metric Matrices
    const metrics = useMemo(() => {
        const stats = { total: leads.length, pending: 0, progress: 0, converted: 0, dropped: 0 };
        leads.forEach(l => {
            const status = String(l.status || '').trim().toLowerCase();
            if (status === 'pending') stats.pending++;
            else if (status === 'in progress') stats.progress++;
            else if (status === 'converted') stats.converted++;
            else if (status === 'not interested') stats.dropped++;
        });
        return stats;
    }, [leads]);

    const patchLeadStatusValue = async (targetLead, freshStatus) => {
        try {
            await API.put(`/leads/${targetLead.id}/status`, {
                status: freshStatus,
                google_row_id: targetLead.google_row_id,
                assigned_to: targetLead.assigned_to
            });

            setLeads(prev => prev.map(item => item.id === targetLead.id ? { ...item, status: freshStatus } : item));
            if (activeLead && activeLead.id === targetLead.id) {
                setActiveLead(prev => ({ ...prev, status: freshStatus }));
            }
            setSystemAlert({ body: `Success: Lead status successfully transitioned to [${freshStatus}].`, type: 'success' });
        } catch (err) {
            setSystemAlert({ body: 'Transaction blocked by remote server parameters.', type: 'error' });
        }
    };

    const processBulkStatusMutation = async (bulkTargetStatus) => {
        setSystemAlert({ body: `Initiating batch pipeline processing for ${selectedRowIds.length} rows...`, type: 'info' });
        let successfullyCommitted = 0;

        for (const itemUuid of selectedRowIds) {
            const targetRowRef = leads.find(l => l.id === itemUuid);
            if (targetRowRef) {
                try {
                    await API.put(`/leads/${targetRowRef.id}/status`, {
                        status: bulkTargetStatus,
                        google_row_id: targetRowRef.google_row_id,
                        assigned_to: targetRowRef.assigned_to
                    });
                    successfullyCommitted++;
                } catch (_) { }
            }
        }

        setLeads(prev => prev.map(row => selectedRowIds.includes(row.id) ? { ...row, status: bulkTargetStatus } : row));
        setSelectedRowIds([]);
        setSystemAlert({ body: `Batch update complete. ${successfullyCommitted} records synchronized successfully.`, type: 'success' });
    };

    const handleCsvExportCommand = () => {
        if (leads.length === 0) return;
        const csvHeaders = ["Lead Name", "Email Id", "Mobile Route", "Target Course Focus", "Host Institution / College", "Status"];
        const outputLines = filteredSalesforceLeads.map(l => [
            l.student_name || l.name,
            l.email || l.email_id,
            l.phone,
            l.course_interest || l.course,
            l.college,
            l.status
        ]);

        const dataPayloadString = "data:text/csv;charset=utf-8," + [csvHeaders.join(","), ...outputLines.map(str => str.join(","))].join("\n");
        const exportDomAnchor = document.createElement('a');
        exportDomAnchor.setAttribute("href", encodeURI(dataPayloadString));
        exportDomAnchor.setAttribute("download", `TechnGlobal_Matrix_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(exportDomAnchor);
        exportDomAnchor.click();
        exportDomAnchor.remove();
    };

    const filteredSalesforceLeads = useMemo(() => {
        return leads.filter(leadItem => {
            const lowQuery = searchQuery.toLowerCase();
            const matchesSearch = String(leadItem.student_name || leadItem.name || '').toLowerCase().includes(lowQuery) ||
                String(leadItem.course_interest || leadItem.course || '').toLowerCase().includes(lowQuery) ||
                String(leadItem.college || '').toLowerCase().includes(lowQuery);

            if (stageFilter === 'ALL') return matchesSearch;
            return matchesSearch && String(leadItem.status || '').trim().toLowerCase() === stageFilter.toLowerCase();
        });
    }, [leads, searchQuery, stageFilter]);

    // Handle Page Resets on Filters
    useEffect(() => { setPage(0); }, [searchQuery, stageFilter]);

    const paginatedLeads = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredSalesforceLeads.slice(start, start + rowsPerPage);
    }, [filteredSalesforceLeads, page, rowsPerPage]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: SF_THEME.canvasBg, overflow: 'hidden', fontFamily: '"Salesforce Sans", -apple-system, sans-serif' }}>

            {/* NAVIGATION BAR */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, bgcolor: SF_THEME.globalHeaderBg, color: '#ffffff', height: '55px', borderBottom: '3px solid #00a1e0', zIndex: 1100 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 14, height: 15, bgcolor: '#00a1e0', transform: 'rotate(45deg)' }} />
                        <Typography sx={{ fontWeight: 900, fontSize: '16px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>TechnGlobal</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 1 }} />
                    <Typography sx={{ fontWeight: 400, fontSize: '13px', color: '#cbd5e1', display: { xs: 'none', sm: 'block' } }}>
                        Lightning Console • {isSupervisorOrHR ? 'Executive Analytical System' : 'Global Matrix Workspace'}
                    </Typography>
                </Stack>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#0176d3', fontSize: '12px', fontWeight: 600 }}>{isSupervisorOrHR ? 'ADM' : 'BDE'}</Avatar>
            </Box>

            {/* HEADER CONSOLE BAR */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', p: 2, bgcolor: SF_THEME.utilityBarBg, borderBottom: `1px solid ${SF_THEME.borderStandard}` }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    {activeLead && !isSupervisorOrHR ? (
                        <IconButton onClick={() => setActiveLead(null)} sx={{ bgcolor: '#ffffff', border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px', p: 1, '&:hover': { bgcolor: '#f4f3f2' } }}>
                            <ArrowLeft size={16} color={SF_THEME.accentBrand} />
                        </IconButton>
                    ) : (
                        <Box sx={{ p: 1.2, bgcolor: '#f2cf5b', borderRadius: '4px', display: 'flex', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <Layers size={20} color="#ffffff" />
                        </Box>
                    )}
                    <Box>
                        <Typography sx={{ color: SF_THEME.textSecondary, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {activeLead ? 'Lead Profile Record Context' : isSupervisorOrHR ? 'HR & Admin Dashboard Control Hub' : 'Leads Analytics Platform'}
                        </Typography>
                        <Typography sx={{ color: SF_THEME.textPrimary, fontSize: '18px', fontWeight: 800 }}>
                            {activeLead && !isSupervisorOrHR ? activeLead.student_name || activeLead.name : isSupervisorOrHR ? 'Cross-Sheet Global Operations Overview' : 'Operational Workspace Pipeline'}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} sx={{ mt: { xs: 2, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                    <Button variant="outlined" onClick={handleCsvExportCommand} startIcon={<Download size={14} />} sx={{ borderColor: SF_THEME.borderStandard, color: SF_THEME.accentBrand, textTransform: 'none', fontWeight: 600, fontSize: '13px', bgcolor: '#ffffff', '&:hover': { bgcolor: '#f4f3f2', borderColor: '#b1afad' } }}>
                        Export CRM dataset
                    </Button>
                    <Button variant="contained" onClick={reloadSalesforceDataRegistry} disabled={loading} startIcon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />} sx={{ bgcolor: SF_THEME.accentBrand, color: '#ffffff', textTransform: 'none', fontWeight: 600, fontSize: '13px', boxShadow: 'none', '&:hover': { bgcolor: SF_THEME.accentHover } }}>
                        Sync Pipeline registry
                    </Button>
                </Stack>
            </Box>

            {/* CORE APP VIEW PORT */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 3, display: 'flex', flexDirection: 'column' }}>
                {systemAlert.body && (
                    <Fade in={Boolean(systemAlert.body)}>
                        <Alert severity={systemAlert.type === 'error' ? "error" : "success"} icon={<Info size={16} />} action={<IconButton size="small" onClick={() => setSystemAlert({ body: '', type: 'info' })}><X size={12} /></IconButton>} sx={{ mb: 2, borderRadius: '4px', fontWeight: 600, fontSize: '12px', borderLeft: `4px solid ${systemAlert.type === 'error' ? SF_THEME.errorRed : SF_THEME.successGreen}`, bgcolor: '#ffffff', color: SF_THEME.textPrimary, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            {systemAlert.body}
                        </Alert>
                    </Fade>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <CircularProgress size={38} sx={{ color: SF_THEME.accentBrand }} />
                    </Box>
                ) : isSupervisorOrHR ? (
                    <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
                        <AdminHrDashboard leads={leads} employeeMap={employeeMap} onSync={reloadSalesforceDataRegistry} />
                    </Box>
                ) : activeLead ? (
                    /* DRILL-DOWN LEAD DETAILS WORKSPACE VIEW */
                    <Fade in={Boolean(activeLead)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%', overflowY: 'auto' }}>
                            {/* Summary Ribbon */}
                            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px', bgcolor: '#ffffff' }}>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                        <Stack direction="row" spacing={2.5} alignItems="center">
                                            <Box sx={{ p: 2, bgcolor: '#4bc076', borderRadius: '4px', display: 'flex' }}>
                                                <Briefcase size={26} color="#ffffff" />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ color: SF_THEME.textSecondary, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target Focus Course Field</Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '24px', color: SF_THEME.textPrimary }}>{activeLead.course_interest || activeLead.course || 'General Inquiry'}</Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ bgcolor: '#fafaf9', p: 2, borderRadius: '4px', border: `1px solid ${SF_THEME.borderStandard}`, ml: { md: 'auto' }, maxWidth: '400px' }}>
                                            <Typography sx={{ fontWeight: 700, color: SF_THEME.textSecondary, mb: 1, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Update Stage State Mapping</Typography>
                                            <Select value={String(activeLead.status || 'Pending').trim()} onChange={(e) => patchLeadStatusValue(activeLead, e.target.value)} size="small" fullWidth sx={{ height: '36px', fontSize: '13px', fontWeight: 700, bgcolor: '#ffffff' }}>
                                                <MenuItem value="Pending">New Lead</MenuItem>
                                                <MenuItem value="In Progress">Working / Inprogress</MenuItem>
                                                <MenuItem value="Converted">Closed - Converted</MenuItem>
                                                <MenuItem value="Not Interested">Lead Lost</MenuItem>
                                            </Select>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Path Matrix Component Indicator */}
                            <Paper elevation={0} sx={{ border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px', overflow: 'hidden', display: 'flex', bgcolor: '#ffffff' }}>
                                {PIPELINE_STAGES.map((stage) => {
                                    const isCurrent = String(activeLead.status || 'Pending').trim().toLowerCase() === stage.toLowerCase();
                                    return (
                                        <Box key={stage} onClick={() => patchLeadStatusValue(activeLead, stage)} sx={{ flex: 1, py: 1.5, px: 2, textAlign: 'center', cursor: 'pointer', fontWeight: 700, fontSize: '12px', transition: 'all 0.2s', bgcolor: isCurrent ? SF_THEME.accentBrand : '#f3f2f1', color: isCurrent ? '#ffffff' : SF_THEME.textSecondary, borderRight: `2px solid #ffffff`, '&:hover': { bgcolor: isCurrent ? SF_THEME.accentHover : '#e0e0e0' } }}>
                                            {stage}
                                        </Box>
                                    );
                                })}
                            </Paper>

                            {/* Detailed Metadata Fields Grid Matrix */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                <Tabs value={activeWorkspaceTab} onChange={(_, val) => setActiveWorkspaceTab(val)} sx={{ borderBottom: `1px solid ${SF_THEME.borderStandard}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '13px', color: SF_THEME.textSecondary, '&.Mui-selected': { color: SF_THEME.accentBrand } }, '& .MuiTabs-indicator': { bgcolor: SF_THEME.accentBrand, height: '3px' } }}>
                                    <Tab label="System Parameters & Contact Details" />
                                </Tabs>
                                <Paper elevation={0} sx={{ bgcolor: '#ffffff', border: `1px solid ${SF_THEME.borderStandard}`, borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                                    <Grid container>
                                        <Grid item xs={12} sm={6} sx={{ borderRight: { sm: `1px solid ${SF_THEME.borderStandard}` } }}>
                                            <LightningDataField fieldLabel="Full Identity Legal Name" fieldValue={activeLead.student_name || activeLead.name} />
                                            <LightningDataField fieldLabel="Configured Email Address" fieldValue={activeLead.email || activeLead.email_id} />
                                            <LightningDataField fieldLabel="Primary Mobile Route Connection" fieldValue={activeLead.phone} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <LightningDataField fieldLabel="College / Affiliated Institute" fieldValue={activeLead.college} />
                                            <LightningDataField fieldLabel="Assigned Account Owner" fieldValue={employeeMap[String(activeLead.assigned_to)] || activeLead.assigned_to || 'Unassigned'} />                                            <LightningDataField fieldLabel="Internal Database Unique UUID" fieldValue={activeLead.id} />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        </Box>
                    </Fade>
                ) : (
                    /* MASTER DASHBOARD OVERVIEW MATRIX WORKSPACE */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%', overflow: 'hidden' }}>

                        {/* GRAPHICAL PIPELINE MATRIX & KPI CARDS SECTION */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={4}>
                                <Grid container spacing={2}>
                                    {[
                                        { title: 'Total Inbound Records', val: metrics.total, icon: <Users size={18} />, color: SF_THEME.accentBrand },
                                        { title: 'Leads Converted', val: metrics.converted, icon: <CheckCircle size={18} />, color: SF_THEME.successGreen }
                                    ].map((card, i) => (
                                        <Grid item xs={6} key={i}>
                                            <Card elevation={0} sx={{ border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px' }}>
                                                <CardContent sx={{ p: '16px !important' }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: SF_THEME.textSecondary, letterSpacing: '0.04em' }}>{card.title}</Typography>
                                                        <Box sx={{ color: card.color }}>{card.icon}</Box>
                                                    </Stack>
                                                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: SF_THEME.textPrimary }}>{card.val}</Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* VISUAL METRIC PIPELINE MATRIX GRAPHIC */}
                            <Grid item xs={12} lg={8}>
                                <Card elevation={0} sx={{ border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px', height: '100%', display: 'flex', alignItems: 'center', px: 2 }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                            <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: SF_THEME.textSecondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TrendingUp size={12} /> Live Graphical Pipeline Segmentation Matrix (Click segment to drill down)
                                            </Typography>
                                            {stageFilter !== 'ALL' && (
                                                <Button size="small" onClick={() => setStageFilter('ALL')} startIcon={<Filter size={12} />} sx={{ fontSize: '11px', p: 0, textTransform: 'none' }}>Clear Segment Filter</Button>
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={0.5} sx={{ height: '36px', width: '100%' }}>
                                            {[
                                                { key: 'Pending', label: 'New', count: metrics.pending, bg: SF_THEME.warningAmber },
                                                { key: 'In Progress', label: 'Working', count: metrics.progress, bg: SF_THEME.accentBrand },
                                                { key: 'Converted', label: 'Converted', count: metrics.converted, bg: SF_THEME.successGreen },
                                                { key: 'Not Interested', label: 'Disqualified', count: metrics.dropped, bg: '#747474' }
                                            ].map((segment) => {
                                                const percentage = metrics.total > 0 ? (segment.count / metrics.total) * 100 : 0;
                                                const isFilterSelected = stageFilter === segment.key;
                                                return (
                                                    <Tooltip title={`${segment.key}: ${segment.count} Leads (${percentage.toFixed(1)}%)`} key={segment.key} arrow>
                                                        <Box onClick={() => setStageFilter(isFilterSelected ? 'ALL' : segment.key)} sx={{ width: percentage > 0 ? `${percentage}%` : '40px', minWidth: '35px', bgcolor: segment.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', border: isFilterSelected ? '3px solid #181818' : 'none', opacity: stageFilter === 'ALL' || isFilterSelected ? 1 : 0.4, borderRadius: '2px', '&:hover': { transform: 'scaleY(1.08)' } }}>
                                                            <Typography sx={{ color: '#ffffff', fontSize: '11px', fontWeight: 700 }}>{segment.count}</Typography>
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* MASTER RECORDS CONSOLE DATA TABLE GRID */}
                        <Paper elevation={0} sx={{ border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px', flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff' }}>
                            <Box sx={{ p: 2, bgcolor: '#fafaf9', borderBottom: `1px solid ${SF_THEME.borderStandard}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Stack direction="row" spacing={2} sx={{ flexGrow: 1, maxWidth: '450px' }} alignItems="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${SF_THEME.borderStandard}`, borderRadius: '4px', px: 1.5, py: 0.6, bgcolor: '#ffffff', width: '100%' }}>
                                        <Search size={14} style={{ color: SF_THEME.textSecondary, marginRight: '8px' }} />
                                        <TextField variant="standard" placeholder="Search Master Matrix Dataset..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ disableUnderline: true, sx: { fontSize: '13px', color: SF_THEME.textPrimary } }} sx={{ width: '100%' }} />
                                        {searchQuery && <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0 }}><X size={14} /></IconButton>}
                                    </Box>
                                    {stageFilter !== 'ALL' && (
                                        <Chip label={`Stage: ${stageFilter}`} size="small" onDelete={() => setStageFilter('ALL')} sx={{ bgcolor: '#e0e5ee', fontWeight: 700, fontSize: '11px' }} />
                                    )}
                                </Stack>

                                <Typography sx={{ fontSize: '12px', color: SF_THEME.textSecondary, fontWeight: 700 }}>
                                    Found Rows: {filteredSalesforceLeads.length} Matrix Records
                                </Typography>
                            </Box>

                            {/* Batch Pipeline Utility Bar Contextual Action */}

                            {selectedRowIds.length > 0 && (

                                <Box sx={{ px: 2, py: 1.5, bgcolor: '#e0e5ee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${SF_THEME.borderStandard}` }}>

                                    <Stack direction="row" alignItems="center" spacing={2}>

                                        <Typography sx={{ fontWeight: 800, fontSize: '13px', color: SF_THEME.textPrimary }}>

                                            {selectedRowIds.length} Object Records Chosen

                                        </Typography>

                                        <Divider orientation="vertical" flexItem sx={{ bgcolor: SF_THEME.borderStandard }} />

                                        <Button size="small" onClick={() => processBulkStatusMutation('In Progress')} sx={{ color: SF_THEME.accentBrand, fontSize: '12px', fontWeight: 700, textTransform: 'none' }}>Set Inprogress</Button>

                                        <Button size="small" onClick={() => processBulkStatusMutation('Converted')} sx={{ color: SF_THEME.successGreen, fontSize: '12px', fontWeight: 700, textTransform: 'none' }}> Set Converted</Button>

                                    </Stack>

                                    <Button size="small" onClick={() => setSelectedRowIds([])} sx={{ color: SF_THEME.textSecondary, textTransform: 'none', fontSize: '12px' }}>Unmark All</Button>

                                </Box>

                            )}



                            {/* Table Data Matrix */}

                            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>

                                <Table size="small" stickyHeader>

                                    <TableHead>

                                        <TableRow sx={{ '& th': { bgcolor: '#fafaf9', color: SF_THEME.textSecondary, fontWeight: 700, fontSize: '11px', borderBottom: `1px solid ${SF_THEME.borderStandard}`, py: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em' } }}>

                                            <TableCell padding="checkbox">

                                                <Checkbox size="small" indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < filteredSalesforceLeads.length} checked={filteredSalesforceLeads.length > 0 && selectedRowIds.length === filteredSalesforceLeads.length} onChange={(e) => e.target.checked ? setSelectedRowIds(filteredSalesforceLeads.map(v => v.id)) : setSelectedRowIds([])} sx={{ color: '#afa9a5', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: SF_THEME.accentBrand } }} />

                                            </TableCell>

                                            <TableCell>Lead Name</TableCell>

                                            <TableCell>Lead Email Id</TableCell>

                                            <TableCell>Lead Phone </TableCell>

                                            <TableCell>Lead Intrested Domain</TableCell>

                                            <TableCell>Lead College</TableCell>

                                            <TableCell>Lead Year of study</TableCell>

                                            <TableCell>Lead Ref CR No.</TableCell>

                                            <TableCell align="center">Status</TableCell>

                                            <TableCell align="right">Actions</TableCell>

                                        </TableRow>

                                    </TableHead>

                                    <TableBody>

                                        {paginatedLeads.length > 0 ? (

                                            paginatedLeads.map((item) => {

                                                const currentStatusConfig = resolveSfStatus(item.status);

                                                return (

                                                    <TableRow key={item.id} hover onClick={() => setActiveLead(item)} sx={{ cursor: 'pointer', '& td': { borderBottom: `1px solid ${SF_THEME.borderStandard}`, py: 1.5, fontSize: '13px' } }}>

                                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>

                                                            <Checkbox size="small" checked={selectedRowIds.includes(item.id)} onChange={() => setSelectedRowIds(prev => prev.includes(item.id) ? prev.filter(x => x !== item.id) : [...prev, item.id])} sx={{ color: '#afa9a5', '&.Mui-checked': { color: SF_THEME.accentBrand } }} />

                                                        </TableCell>

                                                        <TableCell sx={{ fontWeight: 700, color: SF_THEME.accentBrand }}>{item.student_name || '-'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textPrimary }}>{item.email || '—'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textSecondary, fontWeight: 500 }}>{item.phone || '—'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textSecondary }}>{item.course_interest || '—'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textSecondary }}>{item.college || '—'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textSecondary }}>{item.year || '—'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textSecondary }}>{item.cr_number || '—'}</TableCell>

                                                        <TableCell sx={{ color: SF_THEME.textSecondary }}>{item.ref_no || '—'}</TableCell>

                                                        <TableCell align="left">

                                                            <Chip label={currentStatusConfig.label} size="small" sx={{ fontSize: '11px', fontWeight: 700, height: '22px', bgcolor: currentStatusConfig.bg, color: currentStatusConfig.color, borderRadius: '3px', px: 0.5 }} />

                                                        </TableCell>

                                                        <TableCell align="right" sx={{ pr: 2 }}>

                                                            <ChevronRight size={16} color={SF_THEME.borderStandard} />

                                                        </TableCell>

                                                    </TableRow>

                                                );

                                            })

                                        ) : (

                                            <TableRow>

                                                <TableCell colSpan={7} align="center" sx={{ py: 6, color: SF_THEME.textSecondary }}>

                                                    No system records match current query or pipeline filter parameters.

                                                </TableCell>

                                            </TableRow>

                                        )}

                                    </TableBody>

                                </Table>

                            </TableContainer>

                            <TablePagination component="div" count={filteredSalesforceLeads.length} page={page} onPageChange={(_, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50]} sx={{ borderTop: `1px solid ${SF_THEME.borderStandard}`, bgcolor: '#fafaf9', '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '12px', fontWeight: 600, color: SF_THEME.textSecondary } }} />



                        </Paper>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Leads;